// src/App.jsx
import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import TabButton from "./components/TabButton";
import AuthComponent from "./pages/AuthComponent";
import KronosHelper from "./components/KronosHelper";
import { ToastContainer } from "./components/Toast";
import { SubscriptionProvider } from "./SubscriptionContext";
// ── Pages — eager-loaded for instant tab switching (visitedTabs keeps them mounted) ──
import DashboardTab      from "./pages/DashboardTab";
import AboutTab          from "./pages/AboutTab";
import SocialMediaTab    from "./pages/SocialMediaTab";
import ProposalsTab      from "./pages/ProposalsTab";
import CanvasTab         from "./pages/CanvasTab";
import PricingTab        from "./pages/PricingTab";
import AIReceptionistTab from "./pages/AIReceptionistTab";
import "./index.css";

// Legal pages — lazy, rarely visited
const PrivacyPolicy     = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions   = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy      = lazy(() => import("./pages/RefundPolicy"));

// ── Sentry init ───────────────────────────────────────────────────────────────
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV || "production",
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// ── Page loading skeleton — minimal, no animation to prevent flash ────────────
function PageSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6 opacity-30">
      <div className="h-8 w-48 rounded-xl bg-white/5" />
      <div className="h-4 w-full rounded-lg bg-white/5" />
      <div className="h-4 w-3/4 rounded-lg bg-white/5" />
    </div>
  );
}

// Standalone legal pages with minimal chrome for direct URL access
function StandalonePrivacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <PrivacyPolicy />
      </div>
    </div>
  );
}

function StandaloneTerms() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <TermsConditions />
      </div>
    </div>
  );
}

function StandaloneRefund() {
  return (
    <div className="min-h-screen bg-[#0a0a0c] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Kronos AI
          </Link>
        </div>
        <RefundPolicy />
      </div>
    </div>
  );
}

function MainApp() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [token, setToken]         = useState(localStorage.getItem("token"));
  const [username, setUsername]   = useState(localStorage.getItem("username"));

  // Subtle muted colours — used only for active-tab underline, no bloom
  const COLORS = {
    dashboard:         "rgba(100,180,255,0.4)",
    about:             "rgba(100,200,255,0.4)",
    "ai-receptionist": "rgba(160,100,255,0.4)",
    "social-media":    "rgba(220,80,160,0.4)",
    proposals:         "rgba(140,80,255,0.4)",
    pricing:           "rgba(200,160,60,0.4)",
    login:             "rgba(60,200,160,0.4)",
    register:          "rgba(220,100,60,0.4)",
  };

  const handleAuth = ({ access_token, username }) => {
    localStorage.setItem("token", access_token);
    localStorage.setItem("username", username);
    setToken(access_token);
    setUsername(username);
    setActiveTab("dashboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    setToken(null);
    setUsername(null);
    setActiveTab("dashboard");
  };

  useEffect(() => {
    const closeMenu = (e) => { if (!e.target.closest('.user-menu-area')) setShowUserMenu(false); };
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  useEffect(() => {
    const onForceLogout = () => handleLogout();
    const onNavPricing = () => setActiveTab("pricing");
    const onNavSocial  = () => setActiveTab("social-media");
    window.addEventListener("auth:logout", onForceLogout);
    window.addEventListener("nav:pricing", onNavPricing);
    window.addEventListener("nav:social",  onNavSocial);
    return () => {
      window.removeEventListener("auth:logout", onForceLogout);
      window.removeEventListener("nav:pricing", onNavPricing);
      window.removeEventListener("nav:social",  onNavSocial);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderProtected = () => (
    <div className="text-center py-20 text-gray-400">
      <h2 className="text-2xl font-bold mb-4 text-gray-200">
        Please log in to access {activeTab.replace("-", " ")}.
      </h2>
      <TabButton tabName="login" activeTab={activeTab} onClick={setActiveTab} glowColor={COLORS.login}>
        Login
      </TabButton>
    </div>
  );

  const [visitedTabs, setVisitedTabs] = useState(new Set(["dashboard"]));

  useEffect(() => {
    setVisitedTabs(prev => new Set([...prev, activeTab]));
  }, [activeTab]);

  const protectedTabs = ["ai-receptionist", "social-media", "proposals", "doc-studio"];
  const isProtected = !token && protectedTabs.includes(activeTab);

  const renderContent = () => {
    if (isProtected) return renderProtected();

    return (
      <>
        <div style={{ display: activeTab === "dashboard" ? "block" : "none" }}>
          {visitedTabs.has("dashboard") && <DashboardTab onCtaClick={setActiveTab} />}
        </div>
        <div style={{ display: activeTab === "about" ? "block" : "none" }}>
          {visitedTabs.has("about") && <AboutTab onCtaClick={setActiveTab} />}
        </div>
        <div style={{ display: activeTab === "ai-receptionist" ? "block" : "none" }}>
          {visitedTabs.has("ai-receptionist") && <AIReceptionistTab />}
        </div>
        <div style={{ display: activeTab === "social-media" ? "block" : "none" }}>
          {visitedTabs.has("social-media") && <SocialMediaTab />}
        </div>
        <div style={{ display: activeTab === "proposals" ? "block" : "none" }}>
          {visitedTabs.has("proposals") && <ProposalsTab />}
        </div>
        <div style={{ display: activeTab === "doc-studio" ? "block" : "none" }}>
          {visitedTabs.has("doc-studio") && <CanvasTab />}
        </div>
        <div style={{ display: activeTab === "pricing" ? "block" : "none" }}>
          {visitedTabs.has("pricing") && <PricingTab onCtaClick={setActiveTab} />}
        </div>
        <div style={{ display: activeTab === "login" ? "block" : "none" }}>
          {activeTab === "login" && <AuthComponent onAuth={handleAuth} />}
        </div>
        <div style={{ display: activeTab === "register" ? "block" : "none" }}>
          {activeTab === "register" && <AuthComponent register onAuth={handleAuth} />}
        </div>
        <div style={{ display: activeTab === "privacy" ? "block" : "none" }}>
          {activeTab === "privacy" && <PrivacyPolicy />}
        </div>
        <div style={{ display: activeTab === "terms" ? "block" : "none" }}>
          {activeTab === "terms" && <TermsConditions />}
        </div>
      </>
    );
  };

  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <SubscriptionProvider>
    <div className="min-h-screen bg-transparent flex items-center justify-center p-2 sm:p-4 relative overflow-hidden">

      {/* Background is now handled by CSS starry night animation in index.html/index.css */}

      {/* App shell - wider layout for desktop */}
      <div className="relative z-10 w-full max-w-[1600px] bg-[#111114]/40 rounded-xl sm:rounded-2xl border border-white/[0.06] shadow-2xl">

        {/* Header */}
        <header style={{borderBottom: '1px solid rgba(255,255,255,0.08)', width: '100%', background: 'rgba(17,17,20,0.6)', position: 'relative'}}>
          {/* Desktop header - visible on desktop, hidden on mobile */}
          <div className="desktop-header">
            {/* Logo — overflows header intentionally for visual impact */}
            <div className="flex-shrink-0 relative logo-glow" style={{zIndex: 10}}>
              <img
                src="/Kronos-Main.png"
                alt="Kronos Assistant"
                style={{height: "160px", marginTop: "-32px", marginBottom: "-32px"}}
                className="w-auto object-contain logo-glow-img"
              />
            </div>

            {/* Navigation */}
            <nav className="flex gap-1 lg:gap-2 items-center justify-center flex-1">
              {["dashboard","ai-receptionist","social-media","proposals","doc-studio","pricing","about"].map((tab) => (
                <TabButton
                  key={tab}
                  tabName={tab}
                  activeTab={activeTab}
                  onClick={setActiveTab}
                  glowColor={COLORS[tab]}
                >
                  <span className="text-xs lg:text-sm whitespace-nowrap px-1 lg:px-2">
                    {tab.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </TabButton>
              ))}
            </nav>

            {/* Auth Area - Desktop: User Gateway */}
            <div className="flex-shrink-0">
              {!token ? (
                <div className="flex items-center gap-3 px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="hidden lg:block pr-3 border-r border-white/10">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">User Gateway</p>
                    <p className="text-[9px] text-gray-600">Unlock potential</p>
                  </div>
                  <button
                    onClick={() => setActiveTab("login")}
                    className="group flex items-center gap-2 px-4 py-2 rounded-full text-cyan-400 text-sm font-medium transition-all hover:bg-cyan-500/10"
                    style={{ border: "1px solid rgba(6,182,212,0.3)", boxShadow: "0 0 12px rgba(6,182,212,0.1)" }}
                  >
                    <svg className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                    </svg>
                    <span>Log In</span>
                  </button>
                  <button
                    onClick={() => setActiveTab("register")}
                    className="group flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold transition-all hover:scale-105"
                    style={{ background: "linear-gradient(135deg, rgba(6,182,212,0.7), rgba(6,182,212,0.5))", border: "1px solid rgba(6,182,212,0.6)", boxShadow: "0 0 25px rgba(6,182,212,0.3), 0 0 10px rgba(6,182,212,0.2)" }}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                    </svg>
                    <span>Sign Up</span>
                  </button>
                </div>
              ) : (
                <div className="relative user-menu-area">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="user-logged-in-btn flex items-center gap-2.5 px-4 py-2 rounded-full border border-cyan-500/30 bg-black/40 hover:bg-cyan-500/10 transition-all"
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-cyan-400"
                      style={{ background: "linear-gradient(135deg, rgba(6,182,212,0.3), rgba(139,92,246,0.2))", border: "1px solid rgba(6,182,212,0.4)" }}>
                      {(username || "U")[0].toUpperCase()}
                    </div>
                    <span className="text-sm text-gray-300 font-medium max-w-[100px] truncate">{username}</span>
                    <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showUserMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 bg-[#0d1117]/95 backdrop-blur-xl shadow-2xl z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-white/8">
                        <p className="text-xs text-gray-500">Signed in as</p>
                        <p className="text-sm text-white font-semibold truncate">{username}</p>
                      </div>
                      <button onClick={() => { handleLogout(); setShowUserMenu(false); }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile header - visible only on mobile */}
          <div className="mobile-header">
            {/* Top row: Logo and Auth */}
            <div className="flex items-center justify-between px-3 py-1" style={{minHeight: "72px"}}>
              <img
                src="/Kronos-Main.png"
                alt="Kronos Assistant"
                className="w-auto object-contain logo-glow-img"
                style={{ height: "100px", marginTop: "-14px", marginBottom: "-14px" }}
              />
              <div className="flex items-center gap-2">
                {!token ? (
                  <div className="flex gap-2">
                    <button onClick={() => setActiveTab("login")}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500/30 text-cyan-400 text-xs font-semibold">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
                      </svg>
                      Login
                    </button>
                    <button onClick={() => setActiveTab("register")}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                      style={{ background: "linear-gradient(135deg, rgba(6,182,212,0.5), rgba(6,182,212,0.3))", border: "1px solid rgba(6,182,212,0.4)" }}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                      </svg>
                      Sign Up
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-cyan-400"
                      style={{ background: "linear-gradient(135deg, rgba(6,182,212,0.2), rgba(139,92,246,0.15))", border: "1px solid rgba(6,182,212,0.3)" }}>
                      {(username || "U")[0].toUpperCase()}
                    </div>
                    <span className="text-xs text-gray-400 max-w-[60px] truncate">{username}</span>
                    <button onClick={handleLogout}
                      className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-red-400 hover:border-red-500/30">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom row: Navigation tabs - scrollable */}
            <div className="overflow-x-auto scrollbar-hide border-t border-white/[0.08]">
              <nav className="flex gap-1 px-4 py-2 min-w-max">
                {["dashboard","ai-receptionist","social-media","proposals","doc-studio","pricing","about"].map((tab) => (
                  <TabButton
                    key={tab}
                    tabName={tab}
                    activeTab={activeTab}
                    onClick={setActiveTab}
                    glowColor={COLORS[tab]}
                  >
                    <span className="text-xs whitespace-nowrap px-2 py-1">
                      {tab.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </TabButton>
                ))}
              </nav>
            </div>
          </div>
        </header>

        {/* Page content — Suspense wraps all lazy-loaded tabs */}
        <main className="px-4 sm:px-6 md:px-10 lg:px-14 py-6 sm:py-8 min-h-[60vh]">
          <Suspense fallback={<PageSkeleton />}>
            {renderContent()}
          </Suspense>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/[0.08] px-4 sm:px-6 py-4 flex flex-col items-center gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap justify-center">
            <a href="https://github.com/DevX-House" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">GitHub</a>
            <a href="https://pk.linkedin.com/company/devxhouseco" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">LinkedIn</a>
            <a href="https://www.instagram.com/devxhouse/" target="_blank" rel="noreferrer" className="hover:text-cyan-400 transition-colors">Instagram</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Privacy Policy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Terms & Conditions</a>
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">Refund Policy</a>
            <a href="mailto:kronos@devxhouse.com" className="hover:text-cyan-400 transition-colors">Contact</a>
          </div>
          <span className="text-center text-gray-400 font-medium text-xs">
            Built with passion by DevX House
          </span>
          <span>© 2026 Kronos AI. All rights reserved.</span>
        </footer>

      </div>

    </div>
    </SubscriptionProvider>
  );
}

// Main export with routing + Sentry error boundary
export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white p-8">
        <div className="text-center space-y-4 max-w-md">
          <div className="text-5xl">⚠️</div>
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="text-gray-400">We've been notified and are working on a fix.</p>
          <button onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-semibold hover:bg-cyan-500/30 transition-all">
            Reload Page
          </button>
        </div>
      </div>
    }>
      <BrowserRouter>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/privacy" element={<StandalonePrivacy />} />
            <Route path="/terms" element={<StandaloneTerms />} />
            <Route path="/refund" element={<StandaloneRefund />} />
            <Route path="/*" element={<MainApp />} />
          </Routes>
        </Suspense>
        <KronosHelper />
        <ToastContainer />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
